import {isUIService} from '@github-ui/runtime-environment'
import {useEffect} from 'react'

// NOTE: React hook equivalent of the global side-effect import of
// `@github-ui/math-renderer-element` on dotcom.
// In tss-server the global module is not loaded, so <math-renderer>
// custom elements never upgrade and LaTeX is rendered as raw text.
// See https://github.com/github/core-ux/issues/2261

// Selector matches the tags emitted by @github-ui/markdown-math
// (see packages/markdown-math/remark/inline.ts and marked/display.ts).
const MATH_SELECTOR = 'math-renderer.js-inline-math, math-renderer.js-display-math'

let registrationPromise: Promise<unknown> | null = null

function ensureMathRendererRegistered(): Promise<unknown> {
  // The `@controller` decorator in math-renderer-element.ts registers the
  // custom element as a side-effect of module evaluation, so we only need
  // to import it once per page.
  if (customElements.get('math-renderer')) return Promise.resolve()

  // Keep SSR from statically tracing the math renderer module into the
  // server bundle, which would also pull in MathJax and its `url` require.
  registrationPromise ||= import('@github-ui/math-renderer-element')
  return registrationPromise
}

/**
 * Registers the `<math-renderer>` custom element when the current markdown
 * container includes LaTeX nodes. No-op outside the UI Service, where the
 * element is already registered globally.
 */
export function useMathRendererEnrichment(
  containerRef: React.RefObject<HTMLDivElement | null>,
  contentVersion?: unknown,
) {
  useEffect(() => {
    if (!isUIService) return
    const container = containerRef.current
    if (!container) return
    if (!container.querySelector(MATH_SELECTOR)) return

    let cancelled = false
    void (async () => {
      await ensureMathRendererRegistered()
      if (cancelled) return
    })()

    return () => {
      cancelled = true
    }
  }, [containerRef, contentVersion])
}
